/**
 * Tablet Auth Interceptor (Functional)
 *
 * Attaches the two-layer tablet transport credentials to outgoing API calls:
 *   1. `Authorization: Bearer <app-only Azure token>` — the outer layer that lets
 *      Application.cfc's global JWTAzure.validateJWT() gate accept the request.
 *   2. `X-Staff-Session-Token: <badge-session token>` — the inner layer carrying the
 *      badge-verified staff identity + Position ACL.
 *
 * Both are fetched from their respective services and attached only when present.
 * The app-only token is refreshed proactively before expiry by DeviceBootstrapService.
 * The badge-session token is a short-lived (5-minute) server-signed JWT.
 *
 * This interceptor runs alongside the MSAL interceptors; it does not replace them.
 * It only adds the tablet transport headers when the tablet services have values.
 */
import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { from, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { BadgeSessionService } from '../../shell/services/badge-session.service';
import { DeviceBootstrapService } from '../../shell/services/device-bootstrap.service';

/** URL patterns that should never receive tablet transport headers. */
const SKIP_TABLET_AUTH_PATTERNS = [
  '/badges/verify',
  '/badges/msal-verify',
  '/devices/enroll',
  '/devices/register',
  '/devices/status',
  '/devices/token',
  '/login',
  '/logout',
  '/assets/',
  '.json',
];

function shouldSkip(url: string): boolean {
  return SKIP_TABLET_AUTH_PATTERNS.some((pattern) => url.includes(pattern));
}

export const tabletAuthInterceptor: HttpInterceptorFn = (req, next) => {
  if (shouldSkip(req.url)) {
    return next(req);
  }

  const badgeSession = inject(BadgeSessionService);
  const deviceBootstrap = inject(DeviceBootstrapService);

  const sessionToken = badgeSession.token();
  if (!sessionToken) {
    // No badge session — nothing to attach (the request will be rejected server-side
    // if it requires one, which is the correct fail-closed behavior).
    return next(req);
  }

  return from(deviceBootstrap.getAccessToken()).pipe(
    switchMap((appToken) => {
      let headers = req.headers.set('X-Staff-Session-Token', sessionToken);
      // Only set the app-only bearer as the outer transport credential when the
      // request does not already carry a personal MSAL token (set by the MSAL
      // interceptor for supervisor/management endpoints). Overwriting a personal
      // bearer with the app-only token would downgrade a supervisor's session to
      // the lowest tier — never overwrite an existing Authorization header.
      if (appToken && !req.headers.has('Authorization')) {
        headers = headers.set('Authorization', `Bearer ${appToken}`);
      }
      return next(req.clone({ headers }));
    }),
  );
};
